import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceArea } from "recharts";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
}

export default function PatientMarkersChart() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartMarker, setChartMarker] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/patient/my/markers");
        setRecords(data || []);
        if (data && data.length > 0) setChartMarker(data[0].marker_id);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao carregar marcadores.");
      }
      setLoading(false);
    })();
  }, []);

  const uniqueMarkers = useMemo(() => {
    const m = {};
    for (const r of records) if (r.marker_id && !m[r.marker_id]) m[r.marker_id] = { id: r.marker_id, name: r.marker_name };
    return Object.values(m);
  }, [records]);

  const chartData = useMemo(() => {
    if (!chartMarker) return { points: [], ranges: null };
    const rows = records
      .filter((r) => r.marker_id === chartMarker)
      .slice()
      .sort((a, b) => (a.collection_date || "").localeCompare(b.collection_date || ""));
    const ranges = rows[0]?.ranges || {};
    return {
      points: rows.map((r) => ({
        date: fmtDate(r.collection_date), value: r.value, level: r.effective_level,
      })),
      ranges,
      unit: rows[0]?.marker_unit || "",
      name: rows[0]?.marker_name || "",
    };
  }, [chartMarker, records]);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-sm p-8 text-center text-sm text-muted-foreground overline"
        data-testid="patient-markers-chart-loading">
        Carregando…
      </div>
    );
  }

  if (uniqueMarkers.length === 0) {
    return (
      <div className="bg-card border border-border rounded-sm p-8 text-center text-sm text-muted-foreground"
        data-testid="patient-markers-chart-empty">
        Ainda não há marcadores lançados na sua ficha. Quando o farmacêutico registrar valores, sua evolução aparecerá aqui.
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-sm p-4" data-testid="patient-markers-chart">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="overline text-accent inline-flex items-center gap-1">
          <TrendingUp className="w-3 h-3" /> Evolução histórica
        </div>
        <select
          value={chartMarker}
          onChange={(e) => setChartMarker(e.target.value)}
          className="px-3 py-1.5 rounded-sm border border-input bg-card text-xs"
          data-testid="patient-markers-chart-marker-select"
        >
          {uniqueMarkers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>
      {chartData.points.length < 2 ? (
        <div className="py-8 text-center text-xs text-muted-foreground"
          data-testid="patient-markers-chart-insufficient">
          Ainda faltam registros para ver a evolução — pelo menos 2 valores são necessários.
        </div>
      ) : (
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <LineChart data={chartData.points} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              {chartData.ranges?.ideal_min != null && chartData.ranges?.ideal_max != null && (
                <ReferenceArea
                  y1={chartData.ranges.ideal_min}
                  y2={chartData.ranges.ideal_max}
                  fill="hsl(var(--gold))"
                  fillOpacity={0.12}
                  stroke="hsl(var(--gold))"
                  strokeOpacity={0.3}
                />
              )}
              <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v} ${chartData.unit}`, chartData.name]} />
              <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {chartData.ranges?.ideal_min != null && (
        <div className="text-[10px] text-muted-foreground mt-2 text-center"
          data-testid="patient-markers-chart-ideal-label">
          Faixa dourada = zona ideal ({chartData.ranges.ideal_min}–{chartData.ranges.ideal_max} {chartData.unit})
        </div>
      )}
    </div>
  );
}
